import { Flex } from '@chakra-ui/layout';
import { useBreakpoint } from '@chakra-ui/media-query';
import { useDisclosure } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { FeatureFlag, LOGIN, Loading } from '@revelio/core';

import packageJson from '../../package.json';
import { environment } from '../environments/environment';
import useGoogleAnalytics from '../hooks/analytics/useGoogleAnalytics';
import { useBrandMeta } from '../shared/branding/brand-provider';
import { useHideNav } from '../shared/hooks/useHideNav';
import {
  useUnleashFlag,
  useUnleashVersionRefresh,
} from '../shared/unleash-flags/use-unleash-flag';
import '../styles/main.css';

import { PrivateRoute } from './Auth/private-route';
import { useSessionTokenHandoff } from './Auth/use-session-token-handoff';
import { PageManagerProvider } from './CustomDash/components/PageManagerProvider/PageManagerProvider';
import { SharedPlotViewer } from './CustomDash/components/SharedReport/SharedPlotViewer';
import { SharedReportViewer } from './CustomDash/components/SharedReport/SharedReportViewer';
import './CustomDash/styles/print.css';

import { ImpersonationBanner } from './adminRewrite/impersonation/banner/ImpersonationBanner';
import { ImpersonationModal } from './adminRewrite/impersonation/settings/ImpersonationModal';
import { EconomicImpactPage } from './economic-impact/EconomicImpactPage';
import GameV2 from './gameV2/GameV2';
import { GlobalAIAssistantProvider } from './global-ai-assistant/global-ai-assistant-context';
import { GlobalAIAssistantHost } from './global-ai-assistant/global-ai-assistant-host';
import { IndividualPotentialPage } from './individual-potential/IndividualPotentialPage';
import Landing from './landing/Landing';
import Login from './login/login';
import { MobileUnsupportedPage } from './MobileUnsupportedPage';
import './next.module.css';

import type { NavItemConfig, RouteNavConfig } from '@revelio/auth';
import { PagePaths } from '@revelio/core';

import { RequireAuth } from './routes/ProtectedRoute';
import { reducedRoutes } from './routes/reduced-routes';
import { getNavItems } from './routes/routes';
import SideNav from './routes/side-nav/side-nav';
import Tour from './tour/tour';

const POSTING_DISCOVERY_PATHS = new Set<string>([
  PagePaths.POSTING_DISCOVERY,
  `/${PagePaths.POSTING_DISCOVERY}`,
]);

/**
 * Remove the Posting Discovery entry from the nav array recursively. Applied
 * to the side-nav list only — the route tree is still built from the
 * unfiltered nav so direct URL access (and reloads) reach the route element,
 * which handles the flag-off redirect once flags have resolved.
 */
const filterPostingDiscoveryFromNav = (
  items: (RouteNavConfig | NavItemConfig)[]
): (RouteNavConfig | NavItemConfig)[] =>
  items.reduce<(RouteNavConfig | NavItemConfig)[]>((acc, item) => {
    if (item.path && POSTING_DISCOVERY_PATHS.has(item.path)) {
      return acc;
    }
    const navItem = item as RouteNavConfig;
    if (navItem.children) {
      acc.push({
        ...navItem,
        children: filterPostingDiscoveryFromNav(navItem.children),
      });
      return acc;
    }
    acc.push(item);
    return acc;
  }, []);

const version = packageJson.version;

/**
 * Reads `session_token` from the URL on initial mount
 * strip the param from the URL afterwards */
function PrivateRouteWithSessionToken() {
  const { sessionToken, isAwaitingHandoff } = useSessionTokenHandoff();

  if (isAwaitingHandoff) {
    return (
      <Flex height="100vh" bg="white">
        <Loading />
      </Flex>
    );
  }

  return <PrivateRoute sessionToken={sessionToken} />;
}

function GlobalAssistantShell({ children }: { children: ReactNode }) {
  const globalAssistantEnabled = useUnleashFlag(FeatureFlag.GlobalAIAssistant);
  if (globalAssistantEnabled) {
    return (
      <GlobalAIAssistantProvider>
        {children}
        <GlobalAIAssistantHost />
      </GlobalAIAssistantProvider>
    );
  }
  return children;
}

export function Next() {
  useGoogleAnalytics();
  useUnleashVersionRefresh();
  useBrandMeta();
  const isImpersonationEnabled = useUnleashFlag(FeatureFlag.Impersonation);
  const isIndividualPotentialEnabled = useUnleashFlag(
    FeatureFlag.IndividualPotential
  );
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const hideNav = useHideNav();
  const isPostingsDiscoveryEnabled = useUnleashFlag(
    FeatureFlag.PostingDiscovery
  );
  const allNavItems = useMemo(
    () => getNavItems(isNewDashboardLayout),
    [isNewDashboardLayout]
  );
  const navItems = useMemo(
    () =>
      isPostingsDiscoveryEnabled
        ? allNavItems
        : filterPostingDiscoveryFromNav(allNavItems),
    [allNavItems, isPostingsDiscoveryEnabled]
  );
  const routes = useMemo(() => reducedRoutes(allNavItems), [allNavItems]);
  const {
    isOpen: isImpersonationModalOpen,
    onOpen: onImpersonationModalOpen,
    onClose: onImpersonationModalClose,
  } = useDisclosure();
  const handleOpenImpersonationModal = isImpersonationEnabled
    ? onImpersonationModalOpen
    : undefined;

  const breakpoint = useBreakpoint({ ssr: false });
  if (
    environment.production &&
    (breakpoint === 'sm' || breakpoint === 'base')
  ) {
    return <MobileUnsupportedPage />;
  }

  return (
    <>
      <ImpersonationBanner onEditImpersonation={handleOpenImpersonationModal} />
      <Routes>
        <Route path={`/${LOGIN}`} element={<Login />} />
        <Route path="/shared/plot/:token" element={<SharedPlotViewer />} />
        <Route path="/shared/:token" element={<SharedReportViewer />} />
        <Route path="/" element={<PrivateRouteWithSessionToken />}>
          <Route
            element={
              <>
                {!hideNav && <Tour />}
                <PageManagerProvider>
                  <GlobalAssistantShell>
                    <SideNav
                      items={navItems}
                      version={version}
                      onOpenImpersonationModal={handleOpenImpersonationModal}
                    />
                  </GlobalAssistantShell>
                </PageManagerProvider>
              </>
            }
          >
            <Route index element={<Landing />} />

            {routes}

            {/* Admin-beta. The Applications sub-nav only surfaces the link
                when the new-layout flag is on; the route itself is admin-gated
                so it isn't reachable by direct URL for non-admins. Mounted
                inside the layout to keep the dashboard chrome like the other
                apps. */}
            <Route
              path="economic-impact"
              element={
                <RequireAuth adminRequired>
                  <EconomicImpactPage />
                </RequireAuth>
              }
            />
          </Route>
          <Route
            path="/gameV2"
            element={
              <RequireAuth adminRequired>
                <GameV2 />
              </RequireAuth>
            }
          />
          <Route
            path="/individual-potential"
            element={
              isIndividualPotentialEnabled ? (
                <IndividualPotentialPage />
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {isImpersonationEnabled && (
        <ImpersonationModal
          isOpen={isImpersonationModalOpen}
          onClose={onImpersonationModalClose}
        />
      )}
    </>
  );
}

export default Next;
